import { Button, Card, Heading, Link, Paragraph } from "@digdir/designsystemet-react";
import { DownloadIcon } from "@navikt/aksel-icons";
import { AreaContainer } from "../shared/AreaContainer";
import "./Chart.css";
import { Dialog } from "../shared/Dialog.tsx";
import { FlexContainer } from "../shared/FlexContainer.tsx";
import { buildCsvRows, toCSV, triggerDownload, triggerXlsxDownload } from "./charts/chart-export-utils.ts";
import { useChartState } from "./charts/useChartState.ts";
import {
    AreaDescription,
    AreaSeparator,
    ChartContainer,
    ChartIcon,
    InsightCardsContainer,
    IntroSectionContainer,
    NavBreadcrumbs,
    NavContainer,
    NavItem,
    NavItemBackButton,
    NavItemBottomLevel,
    NavItemTopLevel,
} from "./components";
import { effectCardDescriptionIfZero } from "./content.ts";
import EmissionsChart from "./modules/emissions/EmissionsChart";
import {
    EMISSIONS_UNIT,
    formatNumberToTwoDecimals,
    getNavItemValue,
    getNavItemValueAndPercentage,
    getTotalForYear,
} from "./modules/emissions/emissions-chart-utils";
import { EnovaAccumulatedChart } from "./modules/enova-accumulated/EnovaAccumulatedChart";
import { EnovaYearlyChart } from "./modules/enova-yearly/EnovaYearlyChart";
import { MarketChart } from "./modules/market/MarketChart";

export default function Chart() {
    const {
        level,
        selection,
        content,
        currentLevelConfig,
        level1ConfigData,
        level2ConfigData,
        level3ConfigData,
        chartData,
        chartDataWithEffect,
        navItemsForDisplay,
        navItemsForChart,
        enovaSystemKey,
        marketData,
        enovaActivityData,
        hasMarketData,
        canShowEnovaActivityViews,
        resolvedSelectedView,
        isMarketView,
        isAccumulatedView,
        isYearlyView,
        legendHeading,
        normalizedAccumulatedDifferenceValue,
        accumulatedDifference,
        canEnterLevel4,
        effectPatternDotColor,
        effectPatternBackground,
        hoveredNavKey,
        setIsEffectCardHovered,
        setHoveredNavKey,
        setSelectedView,
        getNavCardStyle,
        getBackLabel,
        handleNavClick,
        handleBack,
        handleBreadcrumbNavigate,
    } = useChartState();

    const handleDownloadData = () => {
        if (typeof window === "undefined" || typeof document === "undefined") return;
        triggerDownload(toCSV(buildCsvRows()), "Klimaomstilling.csv");
    };

    const handleDownloadXlsx = () => {
        if (typeof window === "undefined" || typeof document === "undefined") return;
        triggerXlsxDownload();
    };

    return (
        <AreaContainer>
            <Dialog isModal={false}>
                <FlexContainer flexDirection="column" gap="var(--ds-size-4)">
                    <Paragraph>
                        Analysen av forventet utvikling i klimagassutslipp mot 2050 tar utgangspunkt i et datasett fra
                        2022. Det er derfor startåret for analysen, og det betyr at det vil være noen avvik mellom
                        anslåtte og historiske tall i perioden 2022-2025.
                    </Paragraph>
                    <Paragraph>
                        I tillegg er det verdt å være oppmerksom på at analysene våre har en annen inndeling
                        (sosiotekniske system) enn lignende analyser fra Miljødirektoratet, utslippsframskrivingene i
                        Nasjonalbudsjettet og Grønn bok, som bruker tradisjonell sektorinndeling. Analysene og tallene
                        er derfor ikke direkte sammenlignbare.
                    </Paragraph>
                    <Paragraph>
                        Effekten av Enova handler om den langsiktige omstillingseffekten som følger av Enovas
                        midlertidige virkemiddelbruk. Den forventede effekten, forstått som den akkumulerte forskjellen
                        i utslipp over perioden 2022 og 2050 i en situasjon med kontra uten Enova, tar utgangspunkt i at
                        Enova har støtteordninger i et marked helt til opptaket av den nye teknologien eller løsningen
                        kan skje videre av seg selv. At forskjellen i utslipp i de to scenarioene fortsetter helt fram
                        til 2050 handler ikke om at Enova skal være inne i et marked så lenge, men følger av at vi
                        bidrar til å fremskynde og akselerere en utvikling som forventes å gi varige endringer.
                        Analysene våre sier ikke noe om tidspunktet for når Enova trekker seg ut.
                    </Paragraph>
                    <Paragraph>
                        Enova har ikke støtteordninger innen alle transportområdene som er inkludert i analysen. Derfor
                        vil det være noen områder der det kun er ett scenario for utslippsutviklingen.
                    </Paragraph>
                </FlexContainer>
            </Dialog>

            <IntroSectionContainer>
                <Heading level={2} data-size="lg">
                    Hva viser vi?
                </Heading>
                <Paragraph>
                    Nedenfor gir vi innsikt i hvordan vi tror utviklingen i klimagassutslipp kan være med og uten Enova
                    fram mot 2050. Fremstillingen omfatter analyser av gods- og persontransport. Flere områder skal
                    legges til etter hvert. Vi understreker at slike analyser har stor usikkerhet om seg. Klikk deg
                    rundt i verktøyet for å se hva vi tror om omstillingen, og scroll nederst på siden for å få mer
                    informasjon om analysene og metodikken.
                </Paragraph>
                <Paragraph>
                    Fremstillingen inkluderer også en oversikt over hva Enova har gjort innen de ulike
                    transportområdene, og vi forsøker å knytte det sammen med hvordan det kan forventes å bidra til
                    endringer i utslipp og markeder. For mer detaljert oversikt over Enovas aktivitet,{" "}
                    <Link href="https://enova.no/nb/om-oss/resultater/prosjektliste" target="_blank" rel="noreferrer">
                        se egen side for prosjektliste her
                    </Link>
                    .
                </Paragraph>
            </IntroSectionContainer>

            <AreaDescription heading={content.aboveChartHeading} text={content.graphDescription} live>
                {normalizedAccumulatedDifferenceValue > 0 && (
                    <Card
                        className="area-description__card"
                        data-variant="tinted"
                        style={{ ...effectPatternBackground }}
                        onMouseEnter={() => setIsEffectCardHovered(true)}
                        onMouseLeave={() => setIsEffectCardHovered(false)}
                    >
                        <Heading
                            level={3}
                            data-size="xl"
                            style={{
                                color: "var(--ds-color-text-subtle, #5f7775)",
                                fontWeight: "500",
                            }}
                        >
                            {accumulatedDifference} {EMISSIONS_UNIT}
                        </Heading>
                        <Paragraph data-size="xl" style={{ margin: "0.75rem 0 0.25rem" }}>
                            {content.effectCardHeading}
                        </Paragraph>
                        <Paragraph data-size="sm" style={{ margin: 0 }}>
                            {normalizedAccumulatedDifferenceValue > 0
                                ? content.effectCardDescription
                                : effectCardDescriptionIfZero}
                        </Paragraph>
                    </Card>
                )}
            </AreaDescription>

            <a className="skip-link" href="#klimaomstilling-chart-content">
                Hopp til diagraminnhold
            </a>

            <div style={{ width: "100%", marginBottom: "1rem" }}>
                <NavBreadcrumbs
                    level={level}
                    selection={selection}
                    levelConfigs={{
                        level1: level1ConfigData,
                        level2: level2ConfigData,
                        level3: level3ConfigData,
                    }}
                    onNavigate={handleBreadcrumbNavigate}
                />
                <ChartContainer>
                    <NavContainer>
                        <NavItemTopLevel
                            heading={`TOTALT: ${formatNumberToTwoDecimals(getTotalForYear(2024, chartData))} ${EMISSIONS_UNIT} *`}
                        />

                        {level > 1 && <NavItemBackButton heading={getBackLabel(true)} onClick={handleBack} />}

                        {navItemsForDisplay.map((item, displayIndex) => {
                            const value = getNavItemValue(2024, item.area, chartData);
                            const navCardStyle = getNavCardStyle(item, displayIndex);
                            const level2SelectedItem =
                                level === 3 && selection.level2
                                    ? level2ConfigData.navItems.find((nav) => nav.key === selection.level2)
                                    : undefined;
                            const level3SelectedItem =
                                level === 4 && selection.level3
                                    ? level3ConfigData.navItems.find((nav) => nav.key === selection.level3)
                                    : undefined;
                            const navItemText =
                                level === 4 && level3SelectedItem
                                    ? `${getNavItemValueAndPercentage(2024, level3SelectedItem.area, level3ConfigData.data)} *` // TODO: sjekk at det skal være 2024 her
                                    : level === 3 &&
                                        !canEnterLevel4 &&
                                        navItemsForDisplay.length === 1 &&
                                        level2SelectedItem
                                      ? `${getNavItemValueAndPercentage(2024, level2SelectedItem.area, level2ConfigData.data)} *` // TODO: sjekk at det skal være 2024 her
                                      : `${getNavItemValueAndPercentage(2024, item.area, chartData)} *`; // TODO: sjekk at det skal være 2024 her

                            const hoverHandlers = {
                                onMouseEnter: () => setHoveredNavKey(item.key),
                                onMouseLeave: () => setHoveredNavKey(null),
                            };

                            return level < 4 ? (
                                <NavItem
                                    key={item.key}
                                    heading={item.label}
                                    style={navCardStyle}
                                    text={navItemText}
                                    onClick={
                                        value > 0 && (level < 3 || canEnterLevel4)
                                            ? () => handleNavClick(item.key)
                                            : undefined
                                    }
                                    {...hoverHandlers}
                                />
                            ) : (
                                <NavItemBottomLevel
                                    key={item.key}
                                    heading={item.label}
                                    icon={<ChartIcon label={item.label} />}
                                    style={navCardStyle}
                                    text={navItemText}
                                    selectedView={resolvedSelectedView}
                                    onViewChange={setSelectedView}
                                    marketViewAvailable={hasMarketData}
                                    accumulatedViewAvailable={canShowEnovaActivityViews}
                                    yearlyViewAvailable={canShowEnovaActivityViews}
                                />
                            );
                        })}

                        <Paragraph
                            data-size="xs"
                            style={{
                                color: "var(--ds-color-neutral-text-subtle)",
                                marginTop: "0.5rem",
                                whiteSpace: "pre-line",
                            }}
                        >
                            {content.leftBoxText}
                        </Paragraph>
                    </NavContainer>

                    <div className="chart-wrapper" id="klimaomstilling-chart-content" tabIndex={-1}>
                        {isMarketView && hasMarketData ? (
                            <MarketChart data={marketData ?? []} legendHeading={legendHeading} />
                        ) : isAccumulatedView ? (
                            <EnovaAccumulatedChart
                                legendHeading={legendHeading}
                                systemKey={enovaSystemKey}
                                data={enovaActivityData ?? []}
                            />
                        ) : isYearlyView ? (
                            <EnovaYearlyChart
                                legendHeading={legendHeading}
                                systemKey={enovaSystemKey}
                                data={enovaActivityData ?? []}
                            />
                        ) : (
                            <EmissionsChart
                                chartData={chartData}
                                chartDataWithEffect={chartDataWithEffect}
                                level={level}
                                legendHeading={legendHeading}
                                effectPatternDotColor={effectPatternDotColor}
                                currentLevelConfig={currentLevelConfig}
                                navItems={navItemsForChart}
                                normalizedAccumulatedDifferenceValue={normalizedAccumulatedDifferenceValue}
                                hoveredNavKey={hoveredNavKey}
                                onAreaHover={level < 4 ? setHoveredNavKey : undefined}
                            />
                        )}

                        <Paragraph
                            data-size="xs"
                            style={{
                                color: "var(--ds-color-neutral-text-subtle)",
                                marginTop: "0.5rem",
                                fontStyle: "italic",
                                textAlign: "right",
                            }}
                        >
                            Dette er kun estimater og ikke faktiske tall
                        </Paragraph>
                    </div>
                </ChartContainer>
            </div>

            <AreaDescription heading={content.belowChartHeading} text={content.numbersDescription}>
                <FlexContainer flexDirection="column" gap="var(--ds-size-4)" alignItems="flex-start">
                    <Button variant="secondary" aria-label="Last ned CSV-fil" onClick={handleDownloadData}>
                        <DownloadIcon aria-hidden={true} />
                        Last ned CSV-fil
                    </Button>
                    <Button variant="secondary" aria-label="Last ned XLSX-fil" onClick={handleDownloadXlsx}>
                        <DownloadIcon aria-hidden={true} />
                        Last ned XLSX-fil
                    </Button>
                </FlexContainer>
            </AreaDescription>

            <AreaSeparator />

            <InsightCardsContainer>
                <Card
                    data-variant="tinted"
                    data-color="fjordgronn"
                    style={{ backgroundColor: "var(--ds-color-fjordgronn-background-tinted, #e7f7f0)" }}
                >
                    <Heading level={3} data-size="md">
                        Vi deler det vi vet
                    </Heading>
                    <Paragraph>
                        Analysene våre handler om hvordan Norge kan omstille seg til et mer klimavennlig samfunn. Vi
                        deler samfunnet inn etter grunnleggende behov, som person- og godstransport, såkalte
                        sosiotekniske system. Vi ser på hvordan vi tror disse områdene kan utvikle seg fram mot 2050.
                    </Paragraph>
                    <Paragraph>
                        Vi beskriver systemene på en måte som gjør det mulig å beregne hvordan utslippene kan utvikle
                        seg over tid, og hva som påvirker denne utviklingen.
                    </Paragraph>
                    <Paragraph>
                        I første omgang analyseres person- og godstransport. Senere skal det komme flere systemer til,
                        og nye muligheter for å dykke enda mer ned i temaer.
                    </Paragraph>
                </Card>
                <Card
                    data-variant="tinted"
                    data-color="fjellgronn"
                    style={{ backgroundColor: "var(--ds-color-fjellgronn-background-tinted, #f2f4f4)" }}
                >
                    <Heading level={3} data-size="md">
                        Slik går vi fram
                    </Heading>
                    <Paragraph>
                        Analysene bygger på en enkel metode som forklarer hva som påvirker utslipp. Den tar utgangspunkt
                        i tre ting: hvor mange vi er, hva vi etterspør og hvilken teknologi vi bruker. Dette kalles
                        IPAT-likningen. Vi tar utgangspunkt i en variant av denne, Kaya-identiteten. Den viser hvordan
                        energibruk og CO₂ henger sammen. Dette er grunnlaget for beregningene våre.
                    </Paragraph>
                    <Paragraph>
                        Vi deler hvert sosiotekniske system i to deler. Den ene handler om hva som driver utslippene.
                        Den andre handler om hvilke tekniske løsninger som brukes. I persontransport betyr det for
                        eksempel ulike måter å frakte mennesker på.
                    </Paragraph>
                </Card>
                <Card
                    data-variant="tinted"
                    data-color="kraftoransje"
                    style={{ backgroundColor: "var(--ds-color-kraftoransje-background-tinted, #fdf1eb)" }}
                >
                    <Heading level={3} data-size="md">
                        Scenarioer mot 2050
                    </Heading>
                    <Paragraph>
                        Analysene viser hvordan utslippene kan utvikle seg fram mot 2050 i hovedsakelig to scenarioer:
                    </Paragraph>
                    <ul>
                        <li>
                            <strong>Utvikling med Enova:</strong> Slik vi tror utslippene vil utvikle seg med dagens
                            politikk og Enovas virkemidler.
                        </li>
                        <li>
                            <strong>Utvikling uten Enova:</strong> Hvordan vi tror utslippene vil utvikle seg uten
                            Enova.
                        </li>
                    </ul>
                    <Paragraph>
                        Forskjellen mellom utviklingen med og uten Enova viser den langsiktige omstillingseffekten vi
                        tror Enova kan bidra med, gjennom å skape varige endringer i markedet.
                    </Paragraph>
                    <Paragraph>
                        På det øverste nivået har vi inkludert en utslippsbane som viser utviklingen der utslippene er
                        henholdsvis 55, 75 og 95 % lavere i 2030, 2035 og 2050 enn i 1990.
                    </Paragraph>
                    <Paragraph>
                        Hvis du vil lese mer om hvordan vi har gått fram, får du vite mer her:{" "}
                        <Link
                            href="/Klimaomstilling - Metodedokumentasjon vedlegg 1.pdf"
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: "block" }}
                        >
                            Overordnet metodedokumentasjon
                        </Link>
                    </Paragraph>
                </Card>
            </InsightCardsContainer>

            <Card data-variant="tinted" style={{ backgroundColor: "var(--ds-color-background-tinted, #f5f3f1)" }}>
                <Heading level={3} data-size="md">
                    Enovas rolle i klima- og energiomstillingen
                </Heading>
                <Paragraph>
                    Enova er et sentralt virkemiddel for å øke tempoet i klima- og energiomstillingen i Norge. Vi er et
                    statsforetak som eies av Klima- og miljødepartementet og gjennomfører oppdrag på vegne av både
                    Klima- og miljødepartementet og Energidepartementet.
                </Paragraph>
                <Paragraph>
                    Enova utvikler, iverksetter og forvalter støtteprogrammer som fremmer innovasjon ved å bidra til
                    både utvikling av nye løsninger gjennom senfase teknologiutvikling, og til utbredelse av løsninger i
                    markedet. Enovas aktivitet skal innrettes med sikte på å oppnå varige endringer i markeder, slik at
                    løsninger tilpasset lavutslippssamfunnet på sikt blir foretrukket uten støtte. Gjennom varig
                    markedsendring blir den samlede effekten av Enovas innsats langt større enn resultatene fra de
                    enkelte prosjektene vi støtter.
                </Paragraph>
                <Paragraph>
                    Enova kan ikke finansiere tiltak som faller utenfor Klima- og energifondets formål, eller som ikke
                    er i tråd med gjeldende regelverk for statsstøtte i EØS-avtalen. Støtten skal være avgjørende for at
                    den miljøvennlige investeringen blir gjennomført. Enova støtter derfor kun prosjekter som ellers
                    ikke ville blitt gjennomført.
                </Paragraph>
                <Paragraph>
                    Enova inngår i en helhet som et av flere virkemidler på klima- og energifeltet, som samlet skal
                    bidra til den varige markedsendringen og omstillingen som er nødvendig for å nå
                    lavutslippssamfunnet. Etter hvert som nye løsninger og verdikjeder gjøres teknologisk og kommersielt
                    tilgjengelige, vil CO₂-avgift, karbonprising, standardisering, krav og regelverk og andre
                    virkemidler utgjøre rammebetingelsene som gjør det lønnsomt å ta i bruk de nye løsningene i stort
                    monn. Når markedet selv - eller andre offentlige virkemidler - kan drive utviklingen videre, skal
                    Enova trekke seg ut. Slik brukes vårt spissede bidrag midlertidig og målrettet, mens de generelle
                    virkemidlene legger til rette for varig og bred markedsendring og omstilling.
                </Paragraph>
            </Card>
        </AreaContainer>
    );
}
